import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogShell, DialogActions } from './ConfirmDialog';
import RichTextField from './RichTextField';
import ThumbnailField from './ThumbnailField';
import { apiClient } from '../lib/apiClient';
import { richTextToPlain } from '../lib/richText';
import { toast } from '../lib/toast';

export const TITLE_MAX_LENGTH = 120;
export const DESCRIPTION_MAX_CHARS = 5000;

interface EditVideoDetailsDialogProps {
  videoId: string;
  title: string;
  description: string;
  /** Current thumbnail, and where the video can be read from to pick a frame. */
  thumbnailUrl?: string;
  videoUrl?: string;
  duration?: number;
  onSaved: (details: { title: string; description: string; thumbnailUrl?: string }) => void;
  onClose: () => void;
}

/** Owner edits a saved video's title (plain text), description (formatted) and thumbnail. */
const EditVideoDetailsDialog: React.FC<EditVideoDetailsDialogProps> = ({
  videoId, title: initialTitle, description: initialDescription, thumbnailUrl, videoUrl, duration, onSaved, onClose,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [busy, setBusy] = useState(false);
  const [thumbnail, setThumbnail] = useState<Blob | null>(null);
  // Uploaded already (a retry after a failed details save doesn't upload it again).
  const uploadedThumbRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => { titleRef.current?.focus(); }, []);

  const trimmedTitle = title.trim();
  const tooLong = richTextToPlain(description).length > DESCRIPTION_MAX_CHARS;
  const textUnchanged = trimmedTitle === initialTitle.trim() && description === initialDescription;
  const unchanged = textUnchanged && !thumbnail && !uploadedThumbRef.current;

  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!trimmedTitle || tooLong || savingRef.current) return;
    if (unchanged) { onClose(); return; }
    savingRef.current = true;
    setBusy(true);
    try {
      // The thumbnail first: when it fails nothing is saved and the dialog stays open.
      if (thumbnail) {
        const { data, error } = await apiClient.uploadVideoThumbnail(videoId, thumbnail);
        if (error || !data?.thumbnailUrl) {
          toast.error(t('video_thumbnail.save_failed', 'Could not save the thumbnail. Please try again.'));
          return;
        }
        uploadedThumbRef.current = data.thumbnailUrl;
        setThumbnail(null);
      }
      if (!textUnchanged) {
        const { error } = await apiClient.updateVideoDetails(videoId, { title: trimmedTitle, description });
        if (error) {
          toast.error(t('video_details.save_failed', 'Could not save the changes. Please try again.'));
          return;
        }
      }
      toast.success(t('video_details.saved', 'Video details saved.'));
      onSaved({ title: trimmedTitle, description, ...(uploadedThumbRef.current ? { thumbnailUrl: uploadedThumbRef.current } : {}) });
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary, #6b7280)',
  };

  return (
    <DialogShell title={t('video_details.edit_title', 'Edit details')} onCancel={onClose} busy={busy} width={560}>
      <form onSubmit={save}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, paddingRight: 20 }}>
          {t('video_details.edit_title', 'Edit details')}
        </h3>
        <label style={labelStyle}>
          {t('title_label')}
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX_LENGTH}
            disabled={busy}
            required
            style={{
              display: 'block', width: '100%', marginTop: 6, boxSizing: 'border-box',
              padding: '8px 10px', borderRadius: 6, fontSize: 14,
              border: '1px solid var(--theme-border, #d1d5db)',
              background: 'var(--input-bg, var(--card-bg, #fff))',
              color: 'var(--text-primary, #111)',
            }}
          />
        </label>
        <div style={{ ...labelStyle, marginTop: 14 }}>{t('description_label')}</div>
        <RichTextField
          value={description}
          onChange={setDescription}
          placeholder={t('description_placeholder')}
          ariaLabel={t('description_label')}
          maxChars={DESCRIPTION_MAX_CHARS}
          disabled={busy}
        />
        <div style={{ ...labelStyle, marginTop: 14 }}>{t('video_thumbnail.label', 'Thumbnail')}</div>
        <ThumbnailField
          currentUrl={uploadedThumbRef.current || thumbnailUrl}
          videoUrl={videoUrl}
          duration={duration}
          disabled={busy}
          onChange={setThumbnail}
        />
        <DialogActions
          confirmLabel={busy ? t('video_details.saving', 'Saving…') : t('video_details.save', 'Save')}
          cancelLabel={t('cancel', 'Cancel')}
          busy={busy}
          confirmDisabled={!trimmedTitle || tooLong}
          onConfirm={() => save()}
          onCancel={onClose}
        />
      </form>
    </DialogShell>
  );
};

export default EditVideoDetailsDialog;
